import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Typography, Table, Tag, Input, Select, DatePicker, Space, theme, Card, Row, Col, Statistic, Tooltip, Button, App } from 'antd';
import { WarningOutlined, HeartOutlined, FileTextOutlined, BugOutlined, CopyOutlined, DownloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { PLATFORM_COLORS } from '../../utils/constants';
import { Link } from 'react-router';
import { authFetch } from '../../utils/api';
import {
  copyClientLogToClipboard,
  type ClientLogCopyRecord,
} from '../../utils/clipboard';
import { useViewScope } from '../../utils/viewScope';

interface ClientLogStats {
  totalLogs: number;
  crashLogs: number;
  dailyHealth: number;
  byTrigger: Array<{ trigger: string; count: number }>;
  byPlatform: Array<{ platform: string; count: number }>;
  dailyCounts: Array<{ date: string; count: number }>;
}

const MEMBER_STAGES = [
  'TICKET_CONFIG',
  'NODES',
  'ISSUE_TICKET',
  'CATALOGUE_TICKET_CONFIG',
  'CATALOGUE_NODES',
] as const;

type MemberStage = (typeof MEMBER_STAGES)[number];

interface ClientLogDiagnosticsSummary {
  lookbackHours: number;
  topology: { none: number; composite: number; standalone: number };
  anomalies: {
    profile_without_topology: number;
  };
  memberStages: Record<MemberStage, number> & { unclassified: number };
}

const { Title } = Typography;
const { RangePicker } = DatePicker;

interface ClientLogRecord {
  id: number;
  userId: number;
  username?: string | null;
  memberAccountId?: number | null;
  // ha:sha256(ANDROID_ID)[:40] / lt:<uuid> 雜湊值（從 vpn_client join 帶出），
  // 主要供 wudi_star 匿名用戶溯源裝置；匿名 log 或 userId 查無對應時為 null。
  deviceFingerprint?: string | null;
  trigger: string;
  platform: string;
  brand?: string;
  appVersion?: string | null;
  osVersion?: string | null;
  deviceModel?: string | null;
  networkType?: string | null;
  vpnStatus?: string | null;
  lastError?: string | null;
  singboxLog?: string | null;
  appLog?: string | null;
  logContent?: string;
  deviceInfo?: string;
  securityFlags?: string | null;
  // 2.0.53 S53-4 — user 上傳日誌前打的主觀回報文字 (輔助佐證,非 ground-truth)
  userFeedbackText?: string | null;
  createdAt: string;
}

const TRIGGER_COLORS: Record<string, string> = {
  manual: 'blue',
  auto: 'green',
  admin_request: 'purple',
};

/**
 * 安全風險旗標縮寫對照（含嚴重度分級）
 *
 * 由 Android client `TamperCheck.kt` 產生（runtime tamper detection）。
 * **三種旗標都是「異常訊號」（不是「已驗證」），但嚴重度分級不同**：
 *
 * 嚴重度由高至低：
 * - `s` (signingMismatch)  — 紅色高風險。APK 簽名憑證與預期不符，
 *   通常代表客戶安裝的是第三方鏡像 / 重打包版本，而非 K盾官方包。
 *   後端 `CLIENT_SECURITY_RISK_FLAGS` 預設 `s`，命中時 connect-ticket 走
 *   risk-mode（短 TTL 或 deny，視 `CLIENT_SECURITY_RISK_POLICY` 而定）。
 * - `r` (rootFilesystemCompromised) — 橘色中風險。偵測到 root 痕跡
 *   （su binary / Magisk）。中國市場很多 ROM 預裝 root，是常見訊號，
 *   不一定代表惡意；目前後端未強制 deny。
 * - `h` (runtimeCompromised) — 橘色中風險。偵測到 debugger / hook framework
 *   （Frida、Xposed）或惡意套件。dev 機 / 測試機常見，後端未強制 deny。
 *
 * 多旗標時以 `,` 分隔（例：`h,s` 表示同時偵測到 hook + 簽名不符）。
 * iOS / macOS / Windows 客戶端目前未上報，欄位通常為 null。
 *
 * **這些旗標不是「safe 訊號」**：客戶端產生 `h/r/s` 即代表偵測到對應的
 * 異常情境（boolean true）。如果懷疑 K盾 官方簽名 build 仍命中 `s`，
 * 請：(1) 確認該 APK 是從官方下載 (2) 對比客戶端 BuildConfig 期望簽名與
 * 實際 APK 簽名 SHA-256 (3) 若 false positive 是 client 端 bug，需在
 * rpvpn-client 修，不在 BO 重新分類。
 */
type SecuritySeverity = 'high' | 'medium' | 'low';

const SECURITY_FLAG_LABELS: Record<
  string,
  { name: string; desc: string; severity: SecuritySeverity; tagColor: string }
> = {
  s: {
    name: '簽名不符 (high)',
    desc:
      'APK 簽名憑證與預期不符，常見於第三方鏡像 / 重打包版本。' +
      '若客戶聲稱安裝官方 K盾 APK，請確認下載來源 + 比對 BuildConfig 期望簽名。',
    severity: 'high',
    tagColor: 'red',
  },
  r: {
    name: '裝置已 Root (medium)',
    desc: '偵測到 root 痕跡（su binary、Magisk 等）。中國市場常見訊號，未必惡意。',
    severity: 'medium',
    tagColor: 'orange',
  },
  h: {
    name: '執行環境異常 (medium)',
    desc:
      '偵測到 debugger、hook 框架（Frida/Xposed）或惡意套件。' +
      'dev 機 / 測試機 / 客戶模擬器常見訊號。',
    severity: 'medium',
    tagColor: 'orange',
  },
};

const SEVERITY_ORDER: Record<SecuritySeverity, number> = {
  high: 0,
  medium: 1,
  low: 2,
};

function overallSeverity(flags: string[]): SecuritySeverity {
  let best: SecuritySeverity = 'low';
  for (const f of flags) {
    const info = SECURITY_FLAG_LABELS[f];
    if (!info) continue;
    if (SEVERITY_ORDER[info.severity] < SEVERITY_ORDER[best]) {
      best = info.severity;
    }
  }
  return best;
}

function describeSecurityFlags(raw: string): React.ReactNode {
  const flags = raw
    .split(',')
    .map(f => f.trim())
    .filter(Boolean);
  if (flags.length === 0) return null;
  const overall = overallSeverity(flags);
  const overallLabel =
    overall === 'high' ? '高風險' : overall === 'medium' ? '中度訊號' : '低風險';
  return (
    <div style={{ maxWidth: 320 }}>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>
        客戶端偵測到 {flags.length} 項異常訊號（{overallLabel}）
      </div>
      {flags.map(flag => {
        const info = SECURITY_FLAG_LABELS[flag];
        return (
          <div key={flag} style={{ marginBottom: 6 }}>
            <code
              style={{
                background: 'rgba(255,255,255,0.15)',
                padding: '0 4px',
                borderRadius: 2,
                marginRight: 6,
              }}
            >
              {flag}
            </code>
            {info ? (
              <span>
                <strong>{info.name}</strong>
                <div style={{ fontSize: 12, opacity: 0.85 }}>{info.desc}</div>
              </span>
            ) : (
              <span>未知旗標</span>
            )}
          </div>
        );
      })}
      <div
        style={{
          fontSize: 11,
          opacity: 0.75,
          marginTop: 8,
          borderTop: '1px solid rgba(255,255,255,0.2)',
          paddingTop: 6,
        }}
      >
        旗標來源：Android client `TamperCheck.kt`。
        後端 risk-policy 環境變數：CLIENT_SECURITY_RISK_FLAGS / POLICY。
      </div>
    </div>
  );
}

function primaryTagColor(raw: string): string {
  const flags = raw
    .split(',')
    .map(f => f.trim())
    .filter(Boolean);
  // 取嚴重度最高的旗標決定 Tag 顏色；任一 high 即 red，其餘皆 orange
  for (const f of flags) {
    const info = SECURITY_FLAG_LABELS[f];
    if (info?.severity === 'high') return info.tagColor;
  }
  for (const f of flags) {
    const info = SECURITY_FLAG_LABELS[f];
    if (info) return info.tagColor;
  }
  return 'default';
}

function formatDeviceInfo(record: ClientLogRecord): string {
  return [
    record.deviceModel,
    record.osVersion,
    record.networkType,
    record.vpnStatus,
  ].filter(Boolean).join(' / ') || '-';
}

function formatAppVersion(value: string | null | undefined): string {
  if (!value) return '-';
  // 客戶端上報格式為 "<marketing>(<build>)"，例如 "2.0.4(2049)"。
  // 在表格顯示為 "2.0.4 (2049)" 較易讀；非標準格式直接原樣顯示。
  const match = value.match(/^([^()]+)\((.+)\)$/);
  if (match) {
    return `${match[1].trim()} (${match[2].trim()})`;
  }
  return value;
}

function formatLogContent(record: ClientLogRecord): string {
  // 2.0.53 S53-4 — 「=== 用戶回報 ===」放最前面,讓 operator 一眼看到 user 上傳前
  // 打的主觀描述(例:「視頻刷不了」)。這是輔助佐證,不單獨當 ground-truth
  // (per user-testimony-log-reconciliation §B.3 + S53-1a)。
  // 🔴 Codex G2 fix A:必須在 logContent early-return 之前 prepend,否則有
  // logContent 的正常 row(後端已預組好 logContent)永遠看不到 user 回報 section。
  const feedback = record.userFeedbackText?.trim();
  const feedbackBlock = feedback ? `=== 用戶回報 ===\n${feedback}` : '';

  // 後端若已預組好 logContent,直接用它當主體,但仍把 user 回報 prepend 在前。
  if (record.logContent) {
    return feedbackBlock
      ? `${feedbackBlock}\n\n${record.logContent}`
      : record.logContent;
  }

  const sections = [
    feedbackBlock,
    record.singboxLog ? `=== sing-box ===\n${record.singboxLog}` : '',
    record.appLog ? `=== app ===\n${record.appLog}` : '',
  ].filter(Boolean);

  return sections.join('\n\n') || '（無日誌內容）';
}

/**
 * 客戶端日誌頁面 — 伺服器端分頁 + 篩選 + 展開日誌內容
 */
export const ClientLogList: React.FC = () => {
  const { token } = theme.useToken();
  const { message } = App.useApp();
  const { brandForRequest } = useViewScope();
  const [data, setData] = useState<ClientLogRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const requestGeneration = useRef(0);
  const [loadedQueryKey, setLoadedQueryKey] = useState<string | null>(null);

  // Filters
  const [userId, setUserId] = useState('');
  const [trigger, setTrigger] = useState<string>('');
  const [platform, setPlatform] = useState<string>('');
  const [memberStage, setMemberStage] = useState<string>('');
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
  const queryKey = [
    page,
    pageSize,
    userId,
    trigger,
    platform,
    memberStage,
    dateRange?.[0].format('YYYY-MM-DD') ?? '',
    dateRange?.[1].format('YYYY-MM-DD') ?? '',
    brandForRequest ?? 'all',
  ].join('\u001f');
  const queryKeyRef = useRef(queryKey);
  queryKeyRef.current = queryKey;
  const previousBrandForRequest = useRef(brandForRequest);
  const request = useMemo(() => {
    const [
      requestPage,
      requestPageSize,
      requestUserId,
      requestTrigger,
      requestPlatform,
      requestMemberStage,
      startTime,
      endTime,
      brand,
    ] = queryKey.split('\u001f');
    return {
      page: requestPage,
      pageSize: requestPageSize,
      userId: requestUserId,
      trigger: requestTrigger,
      platform: requestPlatform,
      memberStage: requestMemberStage,
      startTime,
      endTime,
      // Aggregate scope deliberately omits ?brand. Existing BO routes use
      // that omission to retain aggregate authorization semantics.
      brand: brand === 'all' ? undefined : brand,
    };
  }, [queryKey]);

  // 詳情快取 — 存整筆 detail（含 singboxLog / appLog 完整內容），expand row 與複製共用
  const [detailCache, setDetailCache] = useState<Record<number, ClientLogRecord>>({});
  const [copyingId, setCopyingId] = useState<number | null>(null);

  // Stats for crash dashboard
  const [stats, setStats] = useState<ClientLogStats | null>(null);
  const [diagnostics, setDiagnostics] = useState<ClientLogDiagnosticsSummary | null>(null);

  const fetchStats = useCallback(async () => {
    try {
      const res = await authFetch('/api/admin/client-logs/stats?days=7');
      const result = await res.json();
      if (result.code === 0 && result.data) {
        setStats(result.data);
      }
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    fetchStats();
    // 每 60 秒自動刷新 stats
    const timer = setInterval(fetchStats, 60_000);
    return () => clearInterval(timer);
  }, [fetchStats]);

  const fetchDiagnostics = useCallback(async () => {
    try {
      const params = new URLSearchParams({ hours: '48' });
      if (brandForRequest) params.set('brand', brandForRequest);
      const res = await authFetch(
        `/api/admin/client-log-diagnostics/summary?${params.toString()}`
      );
      const result = await res.json();
      if (result.code === 0 && result.data) {
        setDiagnostics(result.data);
      }
    } catch {
      // Diagnostics are supplemental; the primary log list remains usable.
    }
  }, [brandForRequest]);

  useEffect(() => {
    fetchDiagnostics();
    const timer = setInterval(fetchDiagnostics, 60_000);
    return () => clearInterval(timer);
  }, [fetchDiagnostics]);

  const fetchData = useCallback(async () => {
    const generation = ++requestGeneration.current;
    setData([]);
    setTotal(0);
    setLoadedQueryKey(null);
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', request.page);
      params.set('pageSize', request.pageSize);
      if (request.brand) params.set('brand', request.brand);
      if (request.userId) params.set('userId', request.userId);
      if (request.trigger) params.set('trigger', request.trigger);
      if (request.platform) params.set('platform', request.platform);
      if (request.memberStage) params.set('memberStage', request.memberStage);
      if (request.startTime) params.set('startTime', request.startTime);
      if (request.endTime) params.set('endTime', request.endTime);

      const res = await authFetch(`/api/admin/client-logs?${params.toString()}`);
      const result = await res.json();
      if (generation !== requestGeneration.current) return;
      if (result.code === 0 && result.data) {
        setData(Array.isArray(result.data.list) ? result.data.list : []);
        setTotal(result.data.total ?? 0);
        setLoadedQueryKey(queryKey);
      }
    } catch {
      // ignore
    } finally {
      if (generation === requestGeneration.current) {
        setLoading(false);
      }
    }
  }, [queryKey, request, brandForRequest]);

  useEffect(() => {
    if (previousBrandForRequest.current === brandForRequest) return;
    previousBrandForRequest.current = brandForRequest;
    setPage(1);
  }, [brandForRequest]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /**
   * 抓單筆完整詳情。回傳 detail（含 singboxLog / appLog full content），失敗回 null。
   * 命中快取直接回，避免重複 request。
   */
  const fetchLogDetail = useCallback(
    async (id: number): Promise<ClientLogRecord | null> => {
      if (detailCache[id]) return detailCache[id];
      try {
        const res = await authFetch(`/api/admin/client-logs/${id}`);
        const result = await res.json();
        if (result.code === 0 && result.data) {
          const detail = result.data as ClientLogRecord;
          setDetailCache((prev) => ({ ...prev, [id]: detail }));
          return detail;
        }
      } catch {
        // ignore
      }
      return null;
    },
    [detailCache]
  );

  /**
   * 複製單筆 client log 完整內容（metadata + appLog + singboxLog + signal）到剪貼簿。
   * 列表不回傳 large log fields，必須先 fetch detail 再組訊息。
   */
  const handleCopy = useCallback(
    async (record: ClientLogRecord) => {
      setCopyingId(record.id);
      try {
        const detail = await fetchLogDetail(record.id);
        if (!detail) {
          message.error('無法取得日誌詳情，複製失敗');
          return;
        }
        // 把列表 row 的欄位（username 等）合併進 detail，避免 detail endpoint 沒回 username
        const merged: ClientLogCopyRecord = {
          ...detail,
          username: detail.username ?? record.username,
        };
        const ok = await copyClientLogToClipboard(merged);
        if (ok) {
          message.success('已複製到剪貼簿');
        } else {
          message.error('複製失敗，請手動選取');
        }
      } finally {
        setCopyingId(null);
      }
    },
    [fetchLogDetail]
  );

  // Reset to page 1 when filters change
  const handleUserIdSearch = (value: string) => {
    setPage(1);
    setUserId(value.trim());
  };

  const handleTriggerChange = (value: string) => {
    setPage(1);
    setTrigger(value);
  };

  const handlePlatformChange = (value: string) => {
    setPage(1);
    setPlatform(value);
  };

  const handleMemberStageChange = (value: string) => {
    setPage(1);
    setMemberStage(value);
  };

  const handleDateChange = (dates: [dayjs.Dayjs, dayjs.Dayjs] | null) => {
    setPage(1);
    setDateRange(dates);
  };

  const handleExportCurrentPage = async () => {
    const exportQueryKey = queryKey;
    const exportGeneration = requestGeneration.current;
    setExporting(true);
    try {
      const { downloadClientLogPage } = await import('./xlsx-writer');
      if (
        exportQueryKey !== queryKeyRef.current ||
        exportGeneration !== requestGeneration.current
      ) return;
      downloadClientLogPage(data, page);
      message.success(`已匯出目前頁 ${data.length} 筆資料`);
    } catch {
      message.error('匯出 Excel 失敗，請重試');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="page-enter">
      <Title level={4} style={{ marginBottom: 16 }} data-testid="client-logs-title">客戶端日誌</Title>

      {/* 統計卡片（最近 7 天）*/}
      {stats && (
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={12} sm={6}>
            <Card size="small">
              <Statistic
                title="總上報數（7 天）"
                value={stats.totalLogs}
                prefix={<FileTextOutlined />}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small">
              <Statistic
                title="Crash / Fatal"
                value={stats.crashLogs}
                prefix={<BugOutlined />}
                valueStyle={{ color: stats.crashLogs > 0 ? '#cf1322' : undefined }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small">
              <Statistic
                title="每日健康報告"
                value={stats.dailyHealth}
                prefix={<HeartOutlined />}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Tooltip
              title={
                <div>
                  {stats.byPlatform.map((p) => (
                    <div key={p.platform}>
                      {p.platform}: {p.count}
                    </div>
                  ))}
                </div>
              }
            >
              <Card size="small">
                <Statistic
                  title="平台分佈"
                  value={stats.byPlatform.length}
                  suffix="個平台"
                  prefix={<WarningOutlined />}
                />
              </Card>
            </Tooltip>
          </Col>
        </Row>
      )}

      {diagnostics && (
        <Card
          size="small"
          title={`Member v3 連線診斷（${diagnostics.lookbackHours} 小時）`}
          style={{ marginBottom: 16 }}
          data-testid="client-log-diagnostics-summary"
        >
          <Row gutter={[16, 12]}>
            <Col xs={12} sm={6}>
              <Statistic
                title="AnyTLS 拓撲 none / composite / standalone"
                value={`${diagnostics.topology.none} / ${diagnostics.topology.composite} / ${diagnostics.topology.standalone}`}
              />
            </Col>
            <Col xs={12} sm={6}>
              <Statistic
                title="有 profile 但 topology=none"
                value={diagnostics.anomalies.profile_without_topology}
                valueStyle={{
                  color: diagnostics.anomalies.profile_without_topology > 0 ? '#cf1322' : '#3f8600',
                }}
              />
            </Col>
            <Col xs={24} sm={6}>
              <div style={{ color: token.colorTextSecondary, marginBottom: 8 }}>
                Member 失敗階段
              </div>
              <Space size={[4, 4]} wrap>
                {MEMBER_STAGES.map((stage) => (
                  <Tag key={stage}>{stage}: {diagnostics.memberStages[stage]}</Tag>
                ))}
                <Tag color="default">未分類: {diagnostics.memberStages.unclassified}</Tag>
              </Space>
            </Col>
          </Row>
        </Card>
      )}

      {/* Filter bar */}
      <Space wrap className="mobile-filters" style={{ marginBottom: 12 }}>
        <Input.Search
          placeholder="用戶 ID"
          allowClear
          onSearch={handleUserIdSearch}
          style={{ width: 160, maxWidth: '100%' }}
          data-testid="client-logs-search"
        />
        <Select
          placeholder="觸發方式"
          allowClear
          style={{ width: 140 }}
          onChange={(v) => handleTriggerChange(v ?? '')}
          options={[
            { label: '全部觸發', value: '' },
            { label: '用戶手動上報 (manual)', value: 'manual' },
            { label: '自動上報 (auto)', value: 'auto' },
            { label: '後台主動 (admin_request)', value: 'admin_request' },
          ]}
        />
        <Select
          placeholder="平台"
          allowClear
          style={{ width: 160 }}
          onChange={(v) => handlePlatformChange(v ?? '')}
          options={[
            { label: '全部平台', value: '' },
            { label: 'Android', value: 'android' },
            { label: 'iOS', value: 'ios' },
            { label: 'Windows', value: 'windows' },
            { label: 'macOS', value: 'macos' },
            { label: 'TV Android', value: 'tv_android' },
            { label: 'TV iOS', value: 'tv_ios' },
          ]}
        />
        <Select
          placeholder="Member 失敗階段"
          allowClear
          style={{ width: 230 }}
          onChange={(v) => handleMemberStageChange(v ?? '')}
          options={[
            { label: '全部階段', value: '' },
            ...MEMBER_STAGES.map((stage) => ({ label: stage, value: stage })),
          ]}
          data-testid="client-logs-member-stage-filter"
        />
        <RangePicker
          onChange={(dates) =>
            handleDateChange(dates as [dayjs.Dayjs, dayjs.Dayjs] | null)
          }
        />
      </Space>

      <Button
        icon={<DownloadOutlined />}
        onClick={handleExportCurrentPage}
        disabled={loading || exporting || data.length === 0 || loadedQueryKey !== queryKey}
        data-testid="client-logs-export-current-page"
        style={{ marginBottom: 12 }}
      >
        匯出目前頁（{data.length} 筆）
      </Button>

      <Table
        dataSource={data}
        loading={loading}
        rowKey="id"
        scroll={{ x: 1020 }}
        data-testid="client-logs-table"
        locale={{
          emptyText: loading ? undefined : (
            <div style={{ padding: '40px 0' }}>
              <p>暫無客戶端日誌</p>
            </div>
          ),
        }}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          pageSizeOptions: ['100', '500', '1000'],
          showTotal: (t) => `共 ${t} 筆`,
          onChange: (nextPage, nextPageSize) => {
            if (nextPageSize !== pageSize) {
              setPageSize(nextPageSize);
              setPage(1);
              return;
            }
            setPage(nextPage);
          },
        }}
        expandable={{
          expandedRowRender: (record: ClientLogRecord) => {
            const detail = detailCache[record.id];
            return (
              <pre
                style={{
                  maxHeight: 400,
                  overflow: 'auto',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                  background: token.colorFillQuaternary,
                  padding: 12,
                  borderRadius: 4,
                  fontSize: 12,
                  margin: 0,
                }}
              >
                {detail ? formatLogContent(detail) : '載入中...'}
              </pre>
            );
          },
          onExpand: (expanded: boolean, record: ClientLogRecord) => {
            if (expanded) {
              void fetchLogDetail(record.id);
            }
          },
        }}
      >
        <Table.Column dataIndex="id" title="ID" width={80} sorter={true} />
        <Table.Column
          dataIndex="memberAccountId"
          title="會員帳戶 ID"
          width={135}
          render={(memberAccountId: ClientLogRecord['memberAccountId']) =>
            memberAccountId != null ? (
              <Link
                to={`/users?memberAccountId=${encodeURIComponent(String(memberAccountId))}`}
                aria-label={`查看會員帳戶 ID ${memberAccountId}`}
              >
                {String(memberAccountId)}
              </Link>
            ) : (
              <span style={{ color: token.colorTextTertiary }}>—</span>
            )
          }
        />
        <Table.Column
          dataIndex="userId"
          title="用戶 ID"
          width={105}
          render={(clientId: ClientLogRecord['userId']) =>
            clientId != null ? String(clientId) : '—'
          }
        />
        <Table.Column
          dataIndex="username"
          title="用戶名稱"
          width={150}
          ellipsis
          render={(username: ClientLogRecord['username']) =>
            username?.trim() || <span style={{ color: token.colorTextTertiary }}>—</span>
          }
        />
        <Table.Column
          dataIndex="deviceFingerprint"
          title={
            <Tooltip title="設備指紋（ha:/lt: 雜湊值，非原始 ANDROID_ID）。主要供 wudi_star 匿名用戶溯源裝置；匿名 log 或查無對應用戶時為「—」。">
              <span style={{ borderBottom: '1px dashed currentColor', cursor: 'help' }}>
                設備指紋
              </span>
            </Tooltip>
          }
          width={180}
          render={(fp: string | null | undefined) => {
            if (!fp) {
              return <span style={{ color: token.colorTextTertiary }}>—</span>;
            }
            return (
              <Typography.Text
                copyable={{ text: fp }}
                ellipsis={{ tooltip: fp }}
                style={{ maxWidth: 150, fontSize: 12, fontFamily: 'monospace' }}
              >
                {fp}
              </Typography.Text>
            );
          }}
        />
        <Table.Column
          dataIndex="trigger"
          title="觸發方式"
          width={100}
          render={(val: string) => (
            <Tag color={TRIGGER_COLORS[val] || 'default'}>{val}</Tag>
          )}
        />
        <Table.Column
          dataIndex="platform"
          title="平台"
          width={120}
          render={(val: string) => (
            <Tag color={PLATFORM_COLORS[val] || 'default'}>{val}</Tag>
          )}
        />
        <Table.Column
          dataIndex="appVersion"
          title="App 版本"
          width={140}
          render={(val: string | null | undefined) => {
            if (!val) {
              return <span style={{ color: token.colorTextTertiary }}>-</span>;
            }
            const formatted = formatAppVersion(val);
            return (
              <Tooltip title={val !== formatted ? `原始: ${val}` : '客戶端上報的 App 版本'}>
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatted}</span>
              </Tooltip>
            );
          }}
        />
        <Table.Column
          title="裝置資訊"
          width={220}
          ellipsis
          render={(_, record: ClientLogRecord) => formatDeviceInfo(record)}
        />
        <Table.Column
          dataIndex="securityFlags"
          title={
            <Tooltip
              title={
                <div style={{ maxWidth: 280 }}>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>
                    客戶端執行環境異常訊號（Android only）
                  </div>
                  <div style={{ fontSize: 12 }}>
                    <strong style={{ color: '#ff7875' }}>s</strong>=簽名不符
                    （高風險，可能是第三方鏡像）；
                    <strong style={{ color: '#ffa940' }}>r</strong>=已 Root（中度）；
                    <strong style={{ color: '#ffa940' }}>h</strong>=hook/debug（中度）。
                    空值代表客戶端未偵測到異常（不等於「已驗證」）。
                  </div>
                </div>
              }
            >
              <span style={{ borderBottom: '1px dashed currentColor', cursor: 'help' }}>
                風險
              </span>
            </Tooltip>
          }
          width={130}
          render={(val: string | null | undefined) =>
            val ? (
              <Tooltip title={describeSecurityFlags(val)}>
                <Tag color={primaryTagColor(val)} style={{ cursor: 'help' }}>
                  {val}
                </Tag>
              </Tooltip>
            ) : (
              <Tooltip title="客戶端未偵測到異常訊號（Android TamperCheck 全 false）">
                <Tag>正常</Tag>
              </Tooltip>
            )
          }
        />
        <Table.Column
          dataIndex="createdAt"
          title="時間"
          width={180}
          sorter={true}
          render={(val: string) =>
            val ? dayjs(val).format('YYYY-MM-DD HH:mm:ss') : '-'
          }
        />
        <Table.Column
          title="操作"
          width={100}
          fixed="right"
          render={(_, record: ClientLogRecord) => (
            <Tooltip title="複製日誌完整內容（metadata + appLog + sing-box log）到剪貼簿">
              <Button
                type="link"
                size="small"
                icon={<CopyOutlined />}
                loading={copyingId === record.id}
                onClick={() => handleCopy(record)}
                data-testid={`client-log-copy-${record.id}`}
              >
                複製全部
              </Button>
            </Tooltip>
          )}
        />
      </Table>
    </div>
  );
};
